import InfiniteScroll from "react-infinite-scroll-component"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useUpdateEffect } from "react-use"

import Table from "@/ui/components/Table"
import AXIOS from "@/requests/_axios"
import * as TrinityAPI from "@/requests/TrinityAPI"
import Button from "@/ui/components/Button"
import Skeleton from "@/ui/components/Skeleton"
import { Checkbox } from "@/ui/components/Checkbox"
import { ModalConfirm } from "@/components/common/element/ModalConfirm"
import { removeEmptyParams } from "@/lib/helper"
import { BALANCE_ROUTE, BALANCE_UPLOAD_ROUTE } from "@/routes"

import { HistoryTableFilters } from "./Filters"
import { BalanceHistoryForm } from "./Form"
import { InvoicesItem } from "./Item"

import { useEffect, useMemo, useState, type FC } from "react"
import {
  BalanceHistoryItem,
  HistoryFiltersDirection,
  HistoryFiltersType,
  type BalanceHistoryFilters
} from "@/types/balances"
import type { ICompanyBank } from "@/types/company/IFullCompanyDto"
import type { IModalMessage } from "@/types/error/IModalMessage"

import style from "../../style.module.scss"
import React from "react"
import { useStore } from "@/stores/RootStoreContext"

type Props = {
  banks: ICompanyBank[]
  companyId: string
  setMessage: (message: IModalMessage | null) => void
}

const tableColumns: string[] = [
  "Дата",
  "Сумма",
  "Тип",
  "Назначение",
  "Счет",
  "Валюта",
  "Контрагент",
  "SWIFT",
  "Банк",
  "Владелец"
]

const defaultFilters: BalanceHistoryFilters = {
  from: "",
  to: "",
  bank_id: "",
  bank_label: "Все",
  contractor: "",
  owner: "",
  direction: HistoryFiltersDirection.All,
  direction_label: "Все",
  type: HistoryFiltersType.All,
  type_label: "Все"
}

export const BalanceHistory: FC<Props> = React.memo(({ banks, companyId, setMessage }) => {
  const { userStore } = useStore()

  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [confirmMessage, setConfirmMessage] = useState<IModalMessage>()
  const [isLoading, setLoading] = useState(true)
  const [invoices, setInvoices] = useState<BalanceHistoryItem[]>([])
  const [page, setPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(0)
  const [checkedIds, setCheckedIds] = useState<number[]>([])
  const [isEditOpen, setEditOpen] = useState(false)

  const isAllChecked = useMemo(() => {
    if (!invoices.length) {
      return false
    }

    return checkedIds.length === invoices.length
  }, [checkedIds, invoices])

  const checkedInvoices = useMemo(() => {
    return invoices.filter(invoice => checkedIds.includes(Number(invoice.id)))
  }, [checkedIds, invoices])

  const filters: BalanceHistoryFilters = useMemo(
    () => ({
      companyId: searchParams.get("companyId") || companyId || "",
      from: searchParams.get("from") || "",
      to: searchParams.get("to") || "",
      bank_id: searchParams.get("bank_id") || "",
      bank_label: searchParams.get("bank_label") || "Все",
      contractor: searchParams.get("contractor") || "",
      owner: searchParams.get("owner") || "",
      direction: searchParams.get("direction") || HistoryFiltersDirection.All,
      direction_label: searchParams.get("direction_label") || "Все",
      type: searchParams.get("type") || HistoryFiltersType.All,
      type_label: searchParams.get("type_label") || "Все"
    }),
    [searchParams, companyId]
  )

  const handleReset = () => {
    setPage(1)
    navigate(BALANCE_ROUTE)
  }

  const setFilters = (newFilters: BalanceHistoryFilters) => {
    const newParams = removeEmptyParams(newFilters)
    setSearchParams(newParams)
  }

  const getList = (nextPage: number = 1, isInitial = false) => {
    const query: BalanceHistoryFilters = {
      ...removeEmptyParams(filters),
      companyId: Number(companyId)
    }

    if (query.type) {
      query.type = query.type.toUpperCase()
    }

    if (query.bank_id) {
      query.bankId = Number(query.bank_id)
    }

    delete query.bank_id
    delete query.bank_label
    delete query.type_label
    delete query.direction_label

    TrinityAPI.getInvoicesHistory(
      nextPage - 1,
      query,
      response => {
        if (response.status === 200) {
          if (isInitial) {
            setInvoices([...response.data.items])
            nextPage === 1 && setTotalPages(response.data.totalPages)
          } else {
            setInvoices(prevItems => [...prevItems, ...response.data.items])
            nextPage === 1 && setTotalPages(response.data.totalPages)
          }
        } else {
          setMessage({ title: "Ошибка!", text: `Ошибка загрузки инвойсов: ${response.data}` })
        }
      },
      () => setLoading(false)
    )
  }

  const handleCheckAll = () => {
    if (isAllChecked) {
      setCheckedIds([])

      return
    }

    const newCheckedIds = invoices.map(el => Number(el.id))
    setCheckedIds(newCheckedIds)
  }

  const handleLoadMoreData = () => {
    setPage(prevPage => {
      const nextPage = prevPage + 1
      getList(nextPage)
      return nextPage
    })
  }

  const handleUpload = () => {
    navigate(BALANCE_UPLOAD_ROUTE)
  }

  const handleFormClose = () => {
    setEditOpen(false)
  }

  const handleCheckboxChange = (id: number, value: boolean) => {
    setCheckedIds(value ? [...checkedIds, id] : checkedIds.filter(el => el !== id))
  }

  const confirmRemove = (val: boolean) => {
    if (val) {
      const promises = checkedIds.map(invoiceId => AXIOS.delete(`/trinity/api/v1/bank-statement/${invoiceId}`))

      Promise.all(promises).finally(() => window.location.reload())
    }
  }

  useEffect(() => {
    if (!(filters.from && !filters.to) && companyId) {
      setPage(() => {
        getList(1, true)

        return 1
      })
    }
  }, [filters, companyId])

  useUpdateEffect(() => {
    if (companyId) {
      setPage(() => {
        setFilters({
          ...defaultFilters,
          companyId
        })

        return 1
      })
    }
  }, [companyId])

  return (
    <>
      {isEditOpen && (
        <BalanceHistoryForm checkedInvoices={checkedInvoices} checkedIds={checkedIds} onClose={handleFormClose} />
      )}
      <ModalConfirm message={confirmMessage} setMessage={() => setConfirmMessage(undefined)} />
      <Table.Wrapper
        title="История операций"
        actionButton={
          <div className={style.historyTableActionsWrapper}>
            {(userStore.isManagerStmtWrite || userStore.isAdmin) && (
              <>
                <Button onClick={handleUpload} className={style.historyTableActionButton}>
                  Загрузить
                </Button>
                <Button
                  onClick={() => setEditOpen(true)}
                  variant="grey"
                  disabled={!checkedIds.length}
                  className={style.historyTableActionButton}
                >
                  Редактировать
                </Button>
                <Button
                  onClick={() => {
                    setConfirmMessage({
                      title: "Удалить операции?",
                      text: `Вы действительно желаете удалить операции ${checkedIds.join(", ")}?`,
                      callbackValue: true,
                      _callback: (val: boolean) => confirmRemove(val)
                    })
                  }}
                  variant="grey"
                  disabled={!checkedIds.length}
                  className={style.historyTableActionButton}
                >
                  Удалить
                </Button>
              </>
            )}
            <Button onClick={handleReset} variant="black" className={style.historyTableActionButton}>
              Очистить фильтры
            </Button>
          </div>
        }
        filters={<HistoryTableFilters banks={banks} filters={filters} setFilters={setFilters} />}
      >
        {isLoading ? (
          <Skeleton rows={4} />
        ) : (
          <InfiniteScroll
            dataLength={invoices.length}
            next={handleLoadMoreData}
            scrollableTarget="scrollable"
            hasMore={page < totalPages}
            loader={<p>Загрузка...</p>}
            style={{ listStyleType: "none" }}
          >
            <div>
              <Table.Header
                firstColumn={
                  <div>
                    <Checkbox checked={isAllChecked} onChange={handleCheckAll} />
                  </div>
                }
                className={style.historyTableRow}
                columns={tableColumns}
              />
              {invoices.length > 0 ? (
                invoices.map(invoice => {
                  const isChecked = checkedIds.includes(invoice.id)

                  return (
                    <InvoicesItem
                      isChecked={isChecked}
                      handleCheckboxChange={handleCheckboxChange}
                      item={invoice}
                      key={invoice.id}
                    />
                  )
                })
              ) : (
                <Table.EmptyRow />
              )}
            </div>
          </InfiniteScroll>
        )}
      </Table.Wrapper>
    </>
  )
})
